import React from 'react'
import { CheckCircle2, ChevronRight } from 'lucide-react'
import { mergeClasses } from '../../helpers/styling'
import { AceEditorInstance, CodeTextEditor } from '../code-text-editor'
import { SvgIcon } from '../svg-icon'
import {
  CodeWidgetAnalysis,
  Evidence,
  Finding,
  findingsWatchedBy,
  GlobalUse,
  publishCheckStatus,
  reviewFindingIds,
  RISKS_BY_FINDING,
  RiskVerdict,
} from '../widgets/CodeWidget/analysis/code-widget-analysis'
import { catalogueEntry } from './catalogue'
import {
  findingKey,
  firstFindingKey,
  FocusRequest,
  openAncestorDetails,
  OTHER_OBSERVATIONS,
  widgetDomId,
} from './focus'
import { buildLineIndex, PageSource } from './page-source'
import { WidgetTree } from './path-tree'
import { RiskSummaryRow, SeverityPill } from './status'
import { STATUS_STYLE } from './status-style'
import { Block, BridgeUsage, ConfigurationFacts, EgressTable, LineLink } from './widget-facts'

type FindingRefs = React.MutableRefObject<Map<string, HTMLDivElement>>

const GLOBAL_KIND_LABEL: Record<GlobalUse['kind'], string> = {
  platform: 'the platform bridge',
  read: 'read; no window.x = in the page',
  defined: 'set by the page',
}

const GLOBAL_KIND_HINT: Record<GlobalUse['kind'], string> = {
  platform: 'Provided by Wistor: the bridge the page talks to the host through.',
  read:
    'Likely a library or the platform. A page can also set it in ways this scan does not follow — through a ' +
    'variable holding window, or code it builds at runtime — so check the lines.',
  defined: 'The page puts this on window itself: directly, or through a function it hands window to.',
}

/**
 * One widget's report.
 *
 * Collapsed, it carries enough to triage without opening anything: where the widget sits, what of the host
 * it touches, and every observation with its description. Open, the report moves to the left and the page
 * source sits beside it on the right, so a line number in the evidence can be read against the line itself.
 */
export function WidgetSection(props: { widget: CodeWidgetAnalysis; index: number; focus: FocusRequest | null }) {
  const { widget, index } = props
  const used = widget.bridgeUsage.filter((entry) => entry.used)
  const reviewIds = React.useMemo(() => reviewFindingIds(widget.risks), [widget.risks])
  const lineIndex = React.useMemo(() => buildLineIndex(widget, reviewIds), [widget, reviewIds])
  /* Findings that answer no named risk. They still get reported, just not under a verdict that does not cover them. */
  const uncategorised = widget.findings.filter((finding) => !RISKS_BY_FINDING[finding.id]?.length)

  /*
   * React state rather than a `<details>`: a details element gives its closed contents no layout, so there is
   * no height to animate from. A grid whose row track goes from 0fr to 1fr animates to the content's own
   * height, and the contents stay in the DOM throughout, which the jump-to-finding effect depends on.
   */
  const [expanded, setExpanded] = React.useState(false)
  const gridRef = React.useRef<HTMLDivElement>(null)
  /* Read by the focus effect, which must not re-run when the card opens or closes. */
  const expandedRef = React.useRef(expanded)
  expandedRef.current = expanded
  const editor = React.useRef<AceEditorInstance>(null)
  const findingRefs: FindingRefs = React.useRef(new Map())
  const [activeLine, setActiveLine] = React.useState<number | null>(null)

  /** Report to source: scroll the page to the line and mark it. Ace counts rows from zero. */
  const goToLine = (line: number) => {
    setActiveLine(line)
    editor.current?.scrollToLine(line - 1, true, true)
    editor.current?.gotoLine(line, 0, true)
  }

  /*
   * A request from the app summary: open this card, put the finding on screen, and take the source to it.
   *
   * Opening starts a 300 ms height transition, and scrolling before it ends lands where the row was while the
   * card was still closed. So a closed card scrolls when its grid has finished growing; an open one scrolls at
   * once. The timer is the fallback for a transition that never runs (reduced motion, no change).
   */
  React.useEffect(
    function scrollToFocusedFinding() {
      const request = props.focus
      if (!request) return

      /* No finding named: the request is just "show me this widget", so the card itself is the target. */
      const card = document.getElementById(widgetDomId(index))
      const row = request.findingId
        ? (findingRefs.current.get(findingKey(request.riskId, request.findingId)) ?? null)
        : null
      const target = row ?? card
      const line = request.findingId
        ? widget.findings.find((finding) => finding.id === request.findingId)?.evidence[0]?.line
        : undefined
      openAncestorDetails(target)

      const scroll = () => {
        target?.scrollIntoView({ block: row ? 'center' : 'start', behavior: 'smooth' })
        if (line) goToLine(line)
      }
      const grid = gridRef.current
      if (expandedRef.current || !grid) {
        scroll()
        return
      }

      setExpanded(true)
      let done = false
      const finish = () => {
        if (done) return
        done = true
        grid.removeEventListener('transitionend', onTransitionEnd)
        window.clearTimeout(fallback)
        scroll()
      }
      const onTransitionEnd = (event: TransitionEvent) => {
        if (event.target === grid && event.propertyName === 'grid-template-rows') finish()
      }
      grid.addEventListener('transitionend', onTransitionEnd)
      const fallback = window.setTimeout(finish, 400)
      return () => {
        done = true
        grid.removeEventListener('transitionend', onTransitionEnd)
        window.clearTimeout(fallback)
      }
    },
    /* Runs per request only: the card's own state is read through expandedRef, not reacted to. */
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [props.focus]
  )

  React.useEffect(
    function syncInert() {
      gridRef.current?.toggleAttribute('inert', !expanded)
    },
    [expanded]
  )

  /** Source to report: find what the clicked row is about and bring it into view, opening its risk if closed. */
  const goToFinding = (row: number) => {
    const line = row + 1
    setActiveLine(line)
    const finding = widget.findings.find((candidate) => candidate.evidence.some((item) => item.line === line))
    if (!finding) return
    const element = findingRefs.current.get(firstFindingKey(widget, finding.id)) ?? null
    openAncestorDetails(element)
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }

  const registerFinding = (key: string) => (element: HTMLDivElement | null) => {
    if (element) findingRefs.current.set(key, element)
    else findingRefs.current.delete(key)
  }

  return (
    <div
      id={widgetDomId(index)}
      /* Only the status rail: the accordion row above carries the name, the placement count and the line count. */
      className={mergeClasses('scroll-mt-2 border-l-4', STATUS_STYLE.risk[publishCheckStatus(widget.risks)].rail)}
    >
      <div className="px-3 py-3">
        <WidgetTree paths={widget.paths} />

        <div className="mt-2 flex flex-wrap items-center gap-1">
          {used.length === 0 ? (
            <span className="text-xs text-muted-foreground">Never calls the host</span>
          ) : (
            <>
              <span className="mr-1 text-xs text-muted-foreground">Makes use of</span>
              {used.map((entry) => (
                <span
                  key={entry.member}
                  title={entry.reachesBackend ? 'Stored data is involved' : undefined}
                  className={mergeClasses(
                    'rounded-sm border border-transparent bg-muted px-1.5 py-0.5 font-mono text-[11px]',
                    {
                      [STATUS_STYLE.attention.chip]: entry.reachesBackend,
                    }
                  )}
                >
                  wistor.{entry.member}
                </span>
              ))}
            </>
          )}
        </div>

        {widget.findings.length > 0 && (
          /* Stays when the card opens: it is the summary of the card, not a stand-in for the detail below. */
          <ul className="mt-2 space-y-1">
            {widget.findings.map((finding) => (
              /* The pill runs in the text rather than as its own column, which a narrow panel cannot spare. */
              <li key={finding.id} className="leading-relaxed">
                <SeverityPill tone={reviewIds.has(finding.id) ? 'review' : finding.severity} />{' '}
                <span>
                  <span className="font-medium">{finding.title}</span>
                  <span className="text-muted-foreground"> — {finding.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/*
       * A direct child of the card, not of the header: a sticky element is held inside its parent's box, and
       * the header's box is three rows tall. From here it follows the reader down a long body and leaves when
       * the card itself ends. A fixed height, so the source pane can stick just below it (top-12).
       */}
      <div
        className={mergeClasses('px-3 pb-3', {
          'sticky top-0 z-10 h-12 border-b border-border bg-card pt-2': expanded,
        })}
      >
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((was) => !was)}
          /* The panel's own colour, the one in its title bar, so the card's single control belongs to the panel. */
          className="flex w-full items-center justify-center gap-1 rounded-sm bg-wistor py-1.5 text-xs font-medium text-wistor-foreground transition-opacity hover:opacity-90"
        >
          <SvgIcon
            icon={ChevronRight}
            size={12}
            className={mergeClasses('shrink-0 transition-transform duration-300', { 'rotate-90': expanded })}
          />
          {expanded ? 'Hide details' : 'Expand for details'}
        </button>
      </div>

      {/*
       * 0fr to 1fr animates to the content's own height. Its child clips the content on the way and needs
       * min-h-0, or the 0fr row stays as tall as its content. Clip rather than hidden: overflow-hidden makes the
       * child the scroll container of the sticky source pane, and since it never scrolls the pane would scroll
       * away with the panel. Collapsed, the contents are inert (syncInert), so Tab does not land on them.
       */}
      <div
        ref={gridRef}
        className={mergeClasses('grid transition-all duration-300 ease-out', {
          'grid-rows-[1fr] opacity-100': expanded,
          'grid-rows-[0fr] opacity-0': !expanded,
        })}
      >
        <div className="min-h-0 overflow-clip">
          {/*
           * Inset on all sides, so what opened reads as contained by the card rather than as more card. A narrow
           * panel cannot spare the inset, so there it is one column on the card itself.
           */}
          <div className="grid grid-cols-1 gap-5 px-3 pb-3 [@container(min-width:56rem)]:mx-4 [@container(min-width:56rem)]:mb-4 [@container(min-width:56rem)]:grid-cols-2 [@container(min-width:56rem)]:rounded-lg [@container(min-width:56rem)]:border [@container(min-width:56rem)]:border-border [@container(min-width:56rem)]:bg-muted/40 [@container(min-width:56rem)]:p-4">
            <div className="min-w-0">
              <Block title="Risks">
                <RiskAccordions
                  risks={widget.risks}
                  findings={widget.findings}
                  reviewIds={reviewIds}
                  activeLine={activeLine}
                  onGoToLine={goToLine}
                  registerFinding={registerFinding}
                />
              </Block>

              {uncategorised.length > 0 && (
                <Block title="Other observations">
                  <p className="mb-2 text-xs text-muted-foreground">
                    Worth reporting, but not one of the named risks — mostly configuration that will not do what it
                    looks like it does.
                  </p>
                  {uncategorised.map((finding) => (
                    <FindingRow
                      key={finding.id}
                      finding={finding}
                      review={reviewIds.has(finding.id)}
                      activeLine={activeLine}
                      onGoToLine={goToLine}
                      innerRef={registerFinding(findingKey(OTHER_OBSERVATIONS, finding.id))}
                    />
                  ))}
                </Block>
              )}

              <Block title="Uses of the host">
                <BridgeUsage widget={widget} onGoToLine={goToLine} />
              </Block>

              {widget.libraries.length > 0 && (
                <Block title="Libraries loaded">
                  <ul className="space-y-1">
                    {widget.libraries.map((library) => (
                      <li key={library.url} className="min-w-0">
                        <span className="font-medium">{library.name}</span>{' '}
                        <span className="text-muted-foreground">{library.version ?? 'version unknown'}</span>
                        <div className="break-all font-mono text-xs text-muted-foreground">{library.url}</div>
                      </li>
                    ))}
                  </ul>
                </Block>
              )}

              {widget.globals.length > 0 && (
                <Block title="Globals">
                  <p className="mb-2 text-xs text-muted-foreground">
                    What the page reaches through <code className="font-mono">window</code> that the browser does not
                    provide. Only explicit <code className="font-mono">window.x</code> access is seen; a bare name
                    needs the parser.
                  </p>
                  <ul className="space-y-1">
                    {widget.globals.map((use) => (
                      <li key={use.name} className="flex flex-wrap items-center gap-2">
                        <code className="font-mono text-xs">{use.name}</code>
                        <span className="cursor-help text-xs text-muted-foreground" title={GLOBAL_KIND_HINT[use.kind]}>
                          {GLOBAL_KIND_LABEL[use.kind]}
                          {use.library ? ` · ${use.library}` : ''}
                        </span>
                        <span className="flex flex-wrap gap-1 font-mono text-xs">
                          {use.evidence.map((item) => (
                            <LineLink key={item.line} line={item.line} active={activeLine === item.line} onGoToLine={goToLine} />
                          ))}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Block>
              )}

              {(widget.egress.length > 0 || widget.backendCalls.length > 0) && (
                <Block title="Network">
                  {widget.backendCalls.length > 0 && (
                    <p className="mb-2">
                      Through the host, to the configured store:{' '}
                      <span className="font-mono text-xs">{widget.backendCalls.join(', ')}</span>
                    </p>
                  )}
                  {widget.egress.length > 0 && <EgressTable targets={widget.egress} onGoToLine={goToLine} />}
                </Block>
              )}

              <Block title="Configuration">
                <ConfigurationFacts widget={widget} />
              </Block>

              {widget.configuration.query && (
                <Block title="Query">
                  <CodeTextEditor
                    readonly
                    name={`diagnostics-query-${widget.widgetId ?? index}`}
                    value={widget.configuration.query}
                    mode="sparql"
                    fileExtension="rq"
                    mimeType="application/sparql-query"
                    collapsedHeight="16rem"
                  />
                </Block>
              )}
            </div>

            <div className="min-w-0">
              <div className="sticky top-12">
                <Block title="Source">
                  {widget.html ? (
                    <PageSource
                      html={widget.html}
                      name={widget.widgetId ?? String(index)}
                      lineIndex={lineIndex}
                      activeLine={activeLine}
                      onEditorLoad={(instance) => {
                        editor.current = instance
                      }}
                      onGutterClick={goToFinding}
                    />
                  ) : (
                    <p className="text-muted-foreground">No page configured.</p>
                  )}
                </Block>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The verdict list for one widget: one accordion per risk, holding the checks that answer it.
 *
 * A check that answers two risks is listed under both, so a reader opening either finds the finding that
 * put it where it is. Cleared checks are listed too, greyed: "nothing found" and "never looked" have to be
 * told apart, and at the level of an individual check that is where the detail lives.
 */
function RiskAccordions(props: {
  risks: RiskVerdict[]
  findings: Finding[]
  reviewIds: Set<string>
  activeLine: number | null
  onGoToLine: (line: number) => void
  registerFinding: (key: string) => (element: HTMLDivElement | null) => void
}) {
  return (
    <div className="space-y-2">
      {props.risks.map((risk) => {
        const watched = findingsWatchedBy(risk.id)
        const fired = props.findings.filter((finding) => watched.includes(finding.id))
        const clear = watched.filter((id) => !fired.some((finding) => finding.id === id))

        return (
          <details key={risk.id} className="group rounded-md border border-border" open={risk.status !== 'clear'}>
            <RiskSummaryRow risk={risk} fired={fired.length} watched={watched.length} />

            <div className="border-t border-border px-3 py-2">
              {fired.map((finding) => (
                <FindingRow
                  key={finding.id}
                  finding={finding}
                  review={props.reviewIds.has(finding.id)}
                  activeLine={props.activeLine}
                  onGoToLine={props.onGoToLine}
                  innerRef={props.registerFinding(findingKey(risk.id, finding.id))}
                />
              ))}

              {clear.length > 0 && (
                <ul className="mt-1 space-y-1">
                  {/* Cleared by definition here, so no pill — what each check looks for is in the catalogue. */}
                  {clear.map((id) => (
                    <li key={id} className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <SvgIcon
                        icon={CheckCircle2}
                        size={12}
                        className={mergeClasses('shrink-0', STATUS_STYLE.risk.clear.iconColor)}
                      />
                      <code className="font-mono">{id}</code>
                      <span>{catalogueEntry(id)?.title}</span>
                      <span>not detected</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </details>
        )
      })}
    </div>
  )
}

function FindingRow(props: {
  finding: Finding
  review: boolean
  activeLine: number | null
  onGoToLine: (line: number) => void
  innerRef: (element: HTMLDivElement | null) => void
}) {
  const { finding } = props
  const tone = props.review ? 'review' : finding.severity

  return (
    <div ref={props.innerRef} className={mergeClasses('mb-2 border-l-2 pl-3', STATUS_STYLE.tone[tone].bar)}>
      <div className="flex flex-wrap items-center gap-2">
        <SeverityPill tone={tone} />
        <span className="font-medium">{finding.title}</span>
        {/* The code, so an observation can be looked up in the catalogue at the top. */}
        <code className="font-mono text-[11px] text-muted-foreground">{finding.id}</code>
      </div>
      <p className="mt-1 leading-relaxed text-muted-foreground">{finding.detail}</p>
      <EvidenceList evidence={finding.evidence} activeLine={props.activeLine} onGoToLine={props.onGoToLine} />
    </div>
  )
}

/* Nothing truncated: a reviewer who has to go back to the config for the rest is doing the job this panel exists to replace. */
function EvidenceList(props: { evidence: Evidence[]; activeLine: number | null; onGoToLine: (line: number) => void }) {
  if (props.evidence.length === 0) return null

  return (
    <ul className="mt-1.5 space-y-0.5">
      {props.evidence.map((item) => (
        <li key={item.line} className="flex gap-2 font-mono text-xs">
          <span className="shrink-0">
            <LineLink line={item.line} active={props.activeLine === item.line} onGoToLine={props.onGoToLine} />
          </span>
          <code className="whitespace-pre-wrap break-all">{item.snippet}</code>
        </li>
      ))}
    </ul>
  )
}
